<template>
  <div :id="getId">
    <RAFTFullScreenContent :header-text="getMaterialTypeLabel">
      <div
        :class="{ 'table-ready': getUpdatingMaterialsStatus === 'success' }"
      />
      <div :class="getEmailIconClass">
        <a
          id="email-link"
          href="#"
          v-if="!inLibrary"
          @click="redirectToEmail"
          title="Email groups owning the listed biological materials"
          ><b-img
            :src="getEmailIconPath"
            width="24"
            height="24"
            fluid
            alt="Click to Email"
        /></a>
      </div>
      <BaseFilters
        @update-filters="debouncedUpdateFilter"
        :filters="getFilters"
      />
      <BaseTable
        :items="getBioMaterials"
        :fields="getFields"
        :total-items="getTotalMaterials"
        :current-page="getCurrentPage"
        :is-busy="getBusy"
        :class-prefix="getId"
        @update-current-page="updateCurrentPage"
        ref="table"
      >
        <template #empty>
          <div class="biologicals-empty-text">
            There are currently no '{{ getMaterialTypeLabel }}' biological
            materials to list.
          </div>
        </template>
        <template #og-audience="data">
          <div class="bio-material-group">
            <a :href="data.data.value.url">
              {{ data.data.value.text }}
            </a>
          </div>
        </template>
        <template #internal-link="data">
          <div class="bio-material-unlinked">
            <a :href="data.data.value.url + '?' + getDestinationQuery">{{
              data.data.value.text
            }}</a>
            <div
              v-if="!inLibrary && data.data.item.parent_reference === null"
              class="bio-material-unlinked dog-ear"
            >
              <!--  -->
            </div>
          </div>
        </template>
        <template #remove-link="data">
          <b-modal
            size="xl"
            scrollable
            :id="getInternalId(data.data.item)"
            :busy="getBusy"
            ok-title="Confirm"
            centered
            @ok="removeMaterial(data.data.item)"
            @cancel="confirmBusy = false"
            @close="confirmBusy = false"
            :title="
              'Please confirm that you wish to remove this ' +
              getMaterialTypeLabel
            "
          >
            <p>{{ getMaterialTypeLabel }}: {{ data.data.item.name }}</p>
          </b-modal>
          <a @click.prevent="removeGroupMaterial(data.data.item)" href="#"
            >Remove</a
          >
        </template>
      </BaseTable>
      <AddNewBioMaterial
        v-if="inLibrary"
        :material-type="materialType"
        :material-type-label="getMaterialTypeLabel"
      />
    </RAFTFullScreenContent>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash/debounce";
import PathMapper from "@/lib/PathMapper";
import BiologicalsListingStore from "../../store/modules/biologicals_listing";
import AddNewBioMaterial from "./AddNewBioMaterial.vue";
import BaseFilters from "../raft/BaseFilters.vue";
import BaseTable from "../raft/BaseTable.vue";
import RAFTFullScreenContent from "../RAFTFullScreenContent.vue";

export default {
  name: "InstitutionalBiologicalsListing",
  components: {
    BaseTable,
    BaseFilters,
    AddNewBioMaterial,
    RAFTFullScreenContent,
  },
  props: {
    materialType: { type: String, required: true },
    inLibrary: { type: Boolean, default: false },
  },
  data() {
    return {
      debouncedUpdateFilter: debounce(this.updateFilters, 300),
    };
  },
  computed: {
    ...mapGetters(["getDestinationQuery"]),
    getMaterialTypeLabel() {
      return this.$get("getMaterialTypeLabel");
    },
    getBioMaterials() {
      return this.$get("getBioMaterials");
    },
    getFields() {
      return this.$get("getFields");
    },
    getTotalMaterials() {
      return this.$get("getTotalMaterials");
    },
    getCurrentPage() {
      return this.$get("getCurrentPage");
    },
    getBusy() {
      return this.$get("getBusy");
    },
    getFilters() {
      let filters = this.$get("getFilters");
      // Remove the parent_reference filter if looking at the materials in library (i.e show on the In Use display).
      if (filters && this.inLibrary) {
        filters = filters.filter((item) => item.key !== "parent_reference");
      }
      return filters;
    },
    getUpdatingMaterialsStatus() {
      return this.$get("getUpdatingMaterialsStatus");
    },
    getModuleName() {
      return "BiologicalsListingStore-" + this.materialType;
    },
    getId() {
      return "group-biologicals-listing-" + this.materialType;
    },
    getEmailIconPath() {
      if (this.disabledEmailIcon) {
        return PathMapper.getIconAssetPath("email-disabled.png", this.$isB4);
      }
      return PathMapper.getIconAssetPath("Message.svg", this.$isB4);
    },
    getEmailIconClass() {
      let div_class = "email-link-wrapper";
      if (this.disabledEmailIcon) {
        div_class += " disabled-icon";
      }
      return div_class;
    },
    disabledEmailIcon() {
      return this.getTotalMaterials === 0;
    },
  },
  methods: {
    getInternalId(item) {
      return (
        "remove-library-material-" +
        item.drupal_internal__id +
        "-" +
        item.drupal_internal__vid
      );
    },
    removeGroupMaterial(item) {
      this.$bvModal.show(this.getInternalId(item));
    },
    initializeListings(data) {
      this.$dispatch("initializeListings", data);
    },
    updateCurrentPage(newPage) {
      this.$dispatch("updateCurrentPage", newPage);
    },
    removeMaterial(item) {
      this.$dispatch("removeMaterial", item.id);
    },
    setDefaultFilters() {
      this.$dispatch("setDefaultFilters");
    },
    updateFilters(filterValues) {
      this.$dispatch("updateFilters", filterValues);
    },
    async redirectToEmail() {
      if (this.disabledEmailIcon) {
        this.$showNotification(
          "warn",
          "The list of related groups is empty and can't be emailed.",
        );
        return;
      }
      const form = document.createElement("form");
      form.method = "post";
      form.action = "/raft_message_page/messaging-group-mailer";
      const list = await this.$get("getMaterialsGroupsList");
      if (Array.isArray(list)) {
        list.forEach((group) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = "nids[]";
          input.value = group.nid;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else {
        this.$showNotification(
          "error",
          "Problem fetching the list of groups to email.",
        );
      }
    },
  },
  created() {
    const store = this.$store;
    const namespace = this.getModuleName;
    this.$store.registerModule(namespace, BiologicalsListingStore());
    this.$get = (prop) => store.getters[namespace + "/" + prop];
    this.$dispatch = (action, payload) =>
      store.dispatch(namespace + "/" + action, payload);

    const refs = this.$refs;
    this.initializeListings({
      materialType: this.materialType,
      displayId: "institutional_listing_table",
      refs: refs,
      // Need to have 1/0 for JSON:API filter.
      inLibrary: Number(this.inLibrary),
      groupNid: 0,
    });
  },
};
</script>

<style lang="css" scoped src="../../css/B3BaseTable.css"></style>
<style lang="css" scoped src="../../css/B3Pagination.css"></style>

<style scoped>
div.col {
  width: 30%;
  float: left;
}
</style>
